import { SeoMeta } from '@/components/seo-meta';
import type { ReactElement, ReactNode } from 'react';
import EatalyHeader from '@/components/eataly-header';
import { fetchPageWithComponentData, type PageWithComponentData } from '@/lib/util/pageLoader.server';
import { fetchPrivacyInfoFromContentAsset } from '@/lib/privacy-info.server';
import { Region } from '@/components/region';
import SkipToMainContent from '@/components/skip-to-main-content';
import { MAIN_CONTENT_ID } from '@/components/skip-to-main-content/constants';
import EatalyBreadcrumbsResolver from '@/components/eataly-breadcrumbs/eataly-breadcrumbs-resolver';
import { useEatalyBreadcrumbs } from '@/components/eataly-breadcrumbs/hooks/useEatalyBreadcrumbs';
import { Skeleton } from '@/components/ui/skeleton';
import PrivacyInfoProvider from '@/providers/privacy-info';
import type { PrivacyInfo } from '@/types/privacy-info';
import NotFoundContent from '@/components/not-found-content';
import { Link } from '@/components/link';
import { Button } from '@/components/ui/button';
import {
    isRouteErrorResponse,
    Outlet,
    useRouteError,
    useRouteLoaderData,
    type LoaderFunctionArgs,
    type ShouldRevalidateFunctionArgs,
} from 'react-router';
import { useTranslation } from 'react-i18next';
import type { ShopperProducts } from '@/scapi';
import { fetchCatalogTree } from '@/lib/api/categories.server';
import { fetchCategoryProductCounts } from '@/lib/api/category-product-counts.server';
import { fetchRootContentFolders, type ContentFolder } from '@/lib/api/content-folders.server';
import type { CategoryProductCounts } from '@/lib/category-product-counts';
import { shouldSkipRevalidationForCartOrWishlistAndNewsletterActions } from '@/lib/should-revalidate';
import { getSitePreferences } from '@/lib/preferences.server';
import { getNavigationPillarRootIds } from '@/lib/navigation-pillars.server';
import type { NavigationPillarRootIds } from '@/lib/navigation-pillars';
import { getNotFoundPage } from '@/lib/not-found';

export type AppLayoutLoaderData = {
    storesFolderIdFromSitePreferences?: string;
    navigationPillarRootIds: NavigationPillarRootIds;
    siteFooter: Promise<PageWithComponentData | null>;
    siteNavigation: Promise<PageWithComponentData | null>;
    privacyInfo: Promise<PrivacyInfo | null>;
    editorialMenu: Promise<PageWithComponentData | null>;
    catalogTree: Promise<ShopperProducts.schemas['Category']>;
    contentFolders: Promise<ContentFolder[]>;
    categoryProductCounts: Promise<CategoryProductCounts | undefined>;
};

/** Revalidate only when navigating away from or to pages that mutate catalog/content data. */
export function shouldRevalidate(args: ShouldRevalidateFunctionArgs) {
    if (shouldSkipRevalidationForCartOrWishlistAndNewsletterActions(args)) {
        return false;
    }

    if (args.formMethod && args.formMethod !== 'GET') {
        return true;
    }
    return false;
}

export async function loader(args: LoaderFunctionArgs): Promise<AppLayoutLoaderData> {
    const siteFooter = fetchPageWithComponentData(
        args,
        {
            pageId: 'site-footer',
        },
        { criticalRegions: [] }
    );
    const siteNavigation = fetchPageWithComponentData(
        args,
        {
            pageId: 'site-navigation',
        },
        { criticalRegions: [] }
    );
    const editorialMenu = fetchPageWithComponentData(
        args,
        {
            pageId: 'editorial-menu',
        },
        { criticalRegions: [] }
    );
    const sitePreferences = await getSitePreferences(args.context);
    const storesFolderIdFromSitePreferences = sitePreferences.storesContentFolderId;

    return {
        storesFolderIdFromSitePreferences,
        navigationPillarRootIds: await getNavigationPillarRootIds(args.context),
        siteFooter,
        siteNavigation,
        editorialMenu,
        catalogTree: fetchCatalogTree(args.context),
        contentFolders: fetchRootContentFolders(args.context),
        categoryProductCounts: fetchCategoryProductCounts(args.context),
        privacyInfo: fetchPrivacyInfoFromContentAsset(args.context),
    };
}

function SiteNavigationSkeleton() {
    return (
        <div
            className="hidden w-full items-center justify-between gap-4 bg-[var(--brown-20)] px-6 py-2 md:flex"
            aria-hidden
            data-testid="site-navigation-skeleton">
            <Skeleton className="h-6 w-[200px] shrink-0 bg-primary-foreground/10" />
            <Skeleton className="h-6 flex-1 bg-primary-foreground/10" />
            <Skeleton className="h-6 w-[200px] shrink-0 bg-primary-foreground/10" />
        </div>
    );
}

type AppChromeProps = {
    loaderData: AppLayoutLoaderData;
    children: ReactNode;
    /** When false, breadcrumbs are omitted (used for the 404 ErrorBoundary). */
    showBreadcrumbs?: boolean;
};

/**
 * Shared storefront chrome: preheader, header, main slot, footer.
 */
function AppChrome({ loaderData, children, showBreadcrumbs = true }: AppChromeProps): ReactElement {
    const {
        storesFolderIdFromSitePreferences,
        navigationPillarRootIds,
        siteFooter,
        siteNavigation,
        editorialMenu,
        catalogTree,
        contentFolders,
        categoryProductCounts,
        privacyInfo,
    } = loaderData;

    const {
        items: breadcrumbItems,
        staleItemsWhilePending,
        breadcrumbBackgroundClass,
        homeTo,
        showHome,
    } = useEatalyBreadcrumbs();

    return (
        <PrivacyInfoProvider privacyInfo={privacyInfo}>
            <SkipToMainContent />
            <Region page={siteNavigation} regionId="preheader" fallbackElement={<SiteNavigationSkeleton />} />
            <EatalyHeader
                editorialMenuPage={editorialMenu}
                catalogTree={catalogTree}
                contentFolders={contentFolders}
                categoryProductCounts={categoryProductCounts}
                storesFolderIdFromSitePreferences={storesFolderIdFromSitePreferences}
                navigationPillarRootIds={navigationPillarRootIds}
            />

            {showBreadcrumbs ? (
                <EatalyBreadcrumbsResolver
                    backgroundClassName={breadcrumbBackgroundClass}
                    items={breadcrumbItems}
                    staleItemsWhilePending={staleItemsWhilePending}
                    homeTo={homeTo}
                    showHome={showHome}
                />
            ) : null}
            <main className="grow " id={MAIN_CONTENT_ID} tabIndex={-1}>
                {children}
            </main>
            <Region page={siteFooter} regionId="footerContent" />
        </PrivacyInfoProvider>
    );
}

/**
 * Simple error UI for non-404 failures caught by the layout ErrorBoundary.
 * Full-chrome 404 is handled separately; root ErrorBoundary remains the last resort
 * when this layout never loaded.
 */
function SimpleLayoutErrorContent({ error }: { error: unknown }): ReactElement {
    const { t } = useTranslation('routeError');

    let status: number | undefined;
    let details: string | undefined;
    let stack: string | undefined;

    if (isRouteErrorResponse(error)) {
        status = error.status;
        details = error.statusText;
    } else if (error instanceof Error) {
        details = error.message;
        stack = error.stack;
    } else if (typeof error === 'string') {
        details = error;
    }

    let title: string;
    let message: string | undefined;
    let secondaryMessage: string | undefined;

    if (status === 403) {
        title = t('403.title');
        message = t('403.message');
        secondaryMessage = t('403.secondaryMessage');
    } else if (status === 500) {
        title = t('500.title');
        message = t('500.message');
        secondaryMessage = t('500.secondaryMessage');
    } else {
        title = t('defaultTitle');
    }

    const hasSecondaryOrDetails = Boolean(secondaryMessage) || Boolean(!status && details);

    return (
        <div
            className="flex items-center justify-center bg-neutral-white px-4 py-12 md:py-16"
            data-testid="layout-error-content">
            <SeoMeta title={title} noIndex />
            <div className="mx-auto w-full max-w-3xl text-center">
                {status ? (
                    <div className="text-error-status heading-headline-m mb-8 font-bold leading-none">{status}</div>
                ) : null}
                <h1
                    className={`heading-headline-m text-neutral-10 ${
                        message || hasSecondaryOrDetails ? 'mb-6' : 'mb-12'
                    }`}>
                    {title}
                </h1>
                {message ? (
                    <p
                        className={`body-copy-l mx-auto max-w-2xl text-neutral-10 ${
                            hasSecondaryOrDetails ? 'mb-4' : 'mb-12'
                        }`}>
                        {message}
                    </p>
                ) : null}
                {secondaryMessage ? (
                    <p className="body-copy-l mx-auto mb-12 max-w-2xl text-neutral-10">{secondaryMessage}</p>
                ) : null}
                {!status && details ? (
                    <p className="body-copy-l mx-auto mb-12 max-w-2xl text-neutral-10">Error: {details}</p>
                ) : null}
                <Button asChild hierarchy="primary" size="large" className="cursor-pointer">
                    <Link to="/">{t('goToHomepage')}</Link>
                </Button>
                {stack ? (
                    <div className="mt-16 rounded-none border border-border bg-muted/30 text-left">
                        <div className="flex items-center border-b border-border px-4 py-3">
                            <h2 className="text-sm font-semibold text-foreground">Stack Trace</h2>
                        </div>
                        <pre className="max-h-80 overflow-auto p-4 font-mono text-xs leading-relaxed text-foreground/90">
                            <code>{stack}</code>
                        </pre>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

/**
 * Catches child-route errors while keeping layout chrome for 404s.
 * The 404 merchandising page travels in the thrown response (see `notFoundResponse`),
 * so it is only fetched when a route actually resolves to a 404.
 * Non-404 errors render a simple message inside the same chrome.
 */
export function ErrorBoundary(): ReactElement {
    const error = useRouteError();
    const loaderData = useRouteLoaderData('routes/_app') as AppLayoutLoaderData | undefined;

    if (loaderData && isRouteErrorResponse(error) && error.status === 404) {
        return (
            <AppChrome loaderData={loaderData} showBreadcrumbs={false}>
                <NotFoundContent notFoundPage={getNotFoundPage(error)} />
            </AppChrome>
        );
    }

    if (loaderData) {
        return (
            <AppChrome loaderData={loaderData} showBreadcrumbs={false}>
                <SimpleLayoutErrorContent error={error} />
            </AppChrome>
        );
    }

    // Layout loader never completed — bubble presentation to root ErrorBoundary is not possible
    // once this boundary caught the error; render a minimal fallback.
    return <SimpleLayoutErrorContent error={error} />;
}

/**
 * Default Layout Route
 *
 * This pathless layout route provides the standard storefront UI structure:
 * - Eataly header with navigation
 * - Main content area (via `<Outlet/>`)
 * - Footer
 *
 * Routes that need this layout should be prefixed with `_app.` in their filename.
 * Routes without default header/footer (e.g., OAuth callbacks) use the `_empty.` prefix instead.
 */
export default function DefaultLayout({ loaderData }: { loaderData: AppLayoutLoaderData }) {
    return (
        <AppChrome loaderData={loaderData}>
            <Outlet />
        </AppChrome>
    );
}
